<script lang="ts" setup>
  import {
    Button,
    ButtonVariant,
    SearchInput,
    SearchInputWithSelect,
    Tooltip,
  } from '@tundr/ds-components';
  import { useLocale } from '@tundr/i18n';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { useDebounceFn } from '@vueuse/core';
  import { computed, inject, ref, watch } from 'vue';
  import {
    getSearchByOptionsInjectionKey,
    useFilters,
    useOnFiltersChange,
  } from '../../../composables';
  import {
    type ActiveFilter,
    type FiltersForTable,
    type SearchTextFilterProps,
    type TextSearchOption,
    useFiltersStore,
  } from '../../../store/';
  import type { Column } from '../../DataTable/DataTable.types';
  import {
    FilterTypes,
    getDefaultSearchByField,
    hasCompactFilters,
  } from '../../../utils';
  import { ColumnVisibilityDropdown } from '../../ColumnVisibilityDropdown';
  import { ActiveFiltersRow, FilterSelector } from '../index';
  import {
    inlineActiveFiltersClass,
    searchInputClass,
    tableFiltersRowClass,
    tableFiltersWrapperClass,
    tableFiltersWrapperRowClass,
    tableTopBarActionsClass,
  } from './TableTopBar.css';
  import type { TableExportAction } from './TableTopBar.types';

  const props = withDefaults(
    defineProps<{
      hideSearch?: boolean;
      filterNamespace?: string;
      searchFieldName?: string;
      searchFieldPlaceholder?: string;
      filtersConfig?: ActiveFilter[];
      searchByOptions?: TextSearchOption[];
      hideableColumns?: boolean;
      columnVisibilityNamespace?: string;
      nonHideableColumns?: string[];
      columns?: Column[];
      exportAction?: TableExportAction;
      compactFilters?: boolean;
      inlineFilters?: boolean;
      emptyDataset?: boolean;
    }>(),
    {
      /*
              Left undefined on purpose: Vue would otherwise cast the absent boolean
              to false and permanently opt the table out of the compact filter bar.
            */
      compactFilters: undefined,
    },
  );

  const emit = defineEmits<{
    editFilters: [filters?: FiltersForTable];
  }>();

  const { t } = useLocale('table');

  const currentFilterStore = useFilters(() => props.filterNamespace);
  const filtersStore = useFiltersStore();

  const isExportDisabled = computed(
    () => !!props.exportAction?.disabled || !!props.emptyDataset,
  );

  const exportDisabledReason = computed(() => {
    if (props.exportAction?.disabled) return props.exportAction.disabledReason;

    return props.emptyDataset
      ? t('filters.disabled_on_empty_dataset.export')
      : undefined;
  });

  const isExportDisabledWithReason = computed(
    () => isExportDisabled.value && !!exportDisabledReason.value,
  );

  const exportTooltipTitle = computed(() =>
    isExportDisabledWithReason.value
      ? undefined
      : props.exportAction?.tooltipLabel,
  );

  const exportAriaLabel = computed(() => {
    if (!props.exportAction) return undefined;

    return isExportDisabledWithReason.value
      ? t('filters.disabled_control_aria_label', {
          label: props.exportAction.tooltipLabel,
          reason: exportDisabledReason.value,
        })
      : props.exportAction.tooltipLabel;
  });

  const configurableFilters = computed(() =>
    (props.filtersConfig ?? []).filter(
      (filter) => filter.filterType !== FilterTypes.TEXT_SEARCH,
    ),
  );

  const isCompact = computed(() =>
    hasCompactFilters(props.filtersConfig, props.compactFilters),
  );

  const hasInlineFilters = computed(
    () => !!props.inlineFilters && isCompact.value,
  );

  const seedCompactFilters = () => {
    if (!isCompact.value || !props.filterNamespace) return;
    const configuredFieldNames = new Set(
      configurableFilters.value.map((filter) => filter.filterFieldName),
    );
    currentFilterStore.getFilters.value
      ?.filter(
        (filter) =>
          filter.filterType !== FilterTypes.TEXT_SEARCH &&
          !configuredFieldNames.has(filter.filterFieldName),
      )
      .forEach((filter) =>
        currentFilterStore.removeFilter(filter.filterFieldName),
      );
    configurableFilters.value.forEach((filter) => {
      const seededFilter = { ...filter, modelValue: undefined };
      currentFilterStore.upsertFilter(seededFilter);
    });
    currentFilterStore.sortFilters(
      configurableFilters.value.map((filter) => filter.filterFieldName),
    );
  };

  watch(() => props.filtersConfig, seedCompactFilters, { immediate: true });

  const searchTextValueInStore = computed(() => {
    return currentFilterStore.getFilterValue(
      props.searchFieldName || FilterTypes.TEXT_SEARCH,
    );
  });

  const searchInputModel = ref(searchTextValueInStore.value);

  /*
          The search text can also change from outside the top bar (the empty
          state offers its own clear action, back/forward rewrites it from the
          URL): follow the store so the input always shows the applied term.
        */
  watch(searchTextValueInStore, (searchText) => {
    searchInputModel.value = searchText ?? '';
  });

  const selectedSearchByInStore = computed(() => {
    if (!props.filterNamespace) return undefined;
    const filters = filtersStore.filters[props.filterNamespace];
    const searchFilter = filters?.find(
      (f) => f.filterType === FilterTypes.TEXT_SEARCH,
    ) as SearchTextFilterProps | undefined;
    return searchFilter?.selectedSearchBy;
  });

  const syncedSearchByOptions = props.filterNamespace
    ? inject(getSearchByOptionsInjectionKey(props.filterNamespace), undefined)
    : undefined;

  const effectiveSearchByOptions = computed(
    () => props.searchByOptions ?? syncedSearchByOptions?.value,
  );

  const getDefaultSearchByOption = () =>
    getDefaultSearchByField(effectiveSearchByOptions.value);

  const selectedSearchBy = ref(
    selectedSearchByInStore.value || getDefaultSearchByOption(),
  );

  watch(selectedSearchByInStore, (fieldName) => {
    selectedSearchBy.value = fieldName || getDefaultSearchByOption();
  });

  const hasSearchByOptions = computed(() => {
    return Boolean(effectiveSearchByOptions.value?.length);
  });

  useOnFiltersChange(
    () => props.filterNamespace,
    (filters) => emit('editFilters', filters),
  );

  const onSearch = useDebounceFn((searchText: string) => {
    searchInputModel.value = searchText;
    currentFilterStore.applyFilterValue({
      filterType: FilterTypes.TEXT_SEARCH,
      value: searchText,
      filterFieldName: props.searchFieldName || FilterTypes.TEXT_SEARCH,
      needleBy: hasSearchByOptions.value ? selectedSearchBy.value : undefined,
    });
  }, 500);

  const onSelectedOptionChange = (fieldName: string) => {
    selectedSearchBy.value = fieldName;
    if (searchInputModel.value) {
      currentFilterStore.applyFilterValue({
        filterType: FilterTypes.TEXT_SEARCH,
        value: searchInputModel.value as string,
        filterFieldName: props.searchFieldName || FilterTypes.TEXT_SEARCH,
        needleBy: fieldName,
      });
    }
  };

  const onClearFilters = () => {
    searchInputModel.value = '';
  };
</script>

<template>
  <div :class="tableFiltersWrapperClass">
    <div :class="tableFiltersRowClass" data-testid="table-top-bar-row">
      <div :class="tableFiltersWrapperRowClass">
        <SearchInputWithSelect
          v-if="!hideSearch && hasSearchByOptions"
          :placeholder="searchFieldPlaceholder"
          clearable
          :class="searchInputClass"
          :name="`search-${filterNamespace}`"
          :model-value="searchInputModel as string"
          :test-id="`search-${filterNamespace}`"
          :options="effectiveSearchByOptions!"
          :selected-option="selectedSearchBy"
          :disabled="emptyDataset"
          @update:modelValue="onSearch"
          @update:selectedOption="onSelectedOptionChange"
        />
        <SearchInput
          v-else-if="!hideSearch"
          :placeholder="searchFieldPlaceholder"
          clearable
          :class="searchInputClass"
          :name="`search-${filterNamespace}`"
          :model-value="searchInputModel as string"
          :test-id="`search-${filterNamespace}`"
          :disabled="emptyDataset"
          @update:modelValue="onSearch"
        />
        <slot name="filterSelector">
          <FilterSelector
            v-if="filtersConfig && filterNamespace && !isCompact"
            :filter-namespace="filterNamespace"
            :filters-config="filtersConfig"
            :disabled="emptyDataset"
          />
        </slot>
        <ActiveFiltersRow
          v-if="filterNamespace && hasInlineFilters"
          :class="inlineActiveFiltersClass"
          persistent
          :filter-namespace="filterNamespace"
          :disabled="emptyDataset"
          @clearFilters="onClearFilters"
        >
          <template #additional-filters>
            <slot name="additional-filters"></slot>
          </template>
        </ActiveFiltersRow>
      </div>
      <div :class="tableTopBarActionsClass">
        <slot name="extra-filters-content"></slot>
        <slot name="extra-action"></slot>
        <Tooltip v-if="exportAction" :title="exportTooltipTitle">
          <Button
            :variant="ButtonVariant.Icon"
            :icon-name="TUNDR_ICONS.DOWNLOAD"
            :aria-label="exportAriaLabel"
            :disabled="isExportDisabled"
            :disabled-reason="exportDisabledReason"
            :test-id="exportAction.testId ?? 'table-export'"
            @click="exportAction.onClick"
          />
        </Tooltip>
        <ColumnVisibilityDropdown
          v-if="hideableColumns && columnVisibilityNamespace && columns"
          :columns="columns"
          :namespace="columnVisibilityNamespace"
          :non-hideable-columns="nonHideableColumns"
        />
      </div>
    </div>
    <ActiveFiltersRow
      v-if="filterNamespace && !hasInlineFilters"
      @clearFilters="onClearFilters"
      :filter-namespace="filterNamespace"
      :persistent="isCompact"
      :disabled="emptyDataset"
    >
      <template #additional-filters>
        <slot name="additional-filters"></slot>
      </template>
    </ActiveFiltersRow>
  </div>
</template>
